// utils/citations.ts

export type DocumentContentType = "docx" | "pdf" | "pptx" | "txt" | "markdown";
export type TabularContentType = "tabular";

type LegacyDocPages = number[] | Array<number | null | undefined>;

type DocPagesObject = {
  overall?: [number | null | undefined, number | null | undefined];
  page_ranges?: Array<[number | null | undefined, number | null | undefined]>;
};

type TabularSheetRangesObject = {
  overall?: [number | null | undefined, number | null | undefined];
  ranges?: Array<[number | null | undefined, number | null | undefined]>;
};

export type NewCitationPacket =
  | {
      type: "document";
      cited_text_preview?: string;
      files: Array<
        | {
            file_name: string;
            file_id: string;
            content_type: DocumentContentType;
            // legacy
            pages?: LegacyDocPages;
            // new (may arrive under one of these keys; support all)
            pages_obj?: DocPagesObject;
            pages_v2?: DocPagesObject;
            pages_compat?: DocPagesObject;
            pagesNew?: DocPagesObject;
          }
        | {
            file_name: string;
            file_id: string;
            content_type: TabularContentType;
            // legacy: sheet -> [ [start,end], ... ]
            sheet_ranges?: Record<string, Array<[number, number]>>;
            // new: sheet -> { overall:[s,e], ranges:[[s,e], ...] }
            sheet_ranges_obj?: Record<string, TabularSheetRangesObject>;
          }
      >;
    }
  | {
      type: "web_search";
      cited_text_preview?: string;
      title?: string;
      url: string;
    };

export type CitationViewItem =
  | {
      kind: "document";
      file_id: string;
      file_name: string;
      content_type: DocumentContentType;
      overall?: [number, number] | null;
      page_ranges?: Array<[number, number]>;
      // kept for older UI bits; derived from ranges/overall
      pages?: number[];
    }
  | {
      kind: "tabular";
      file_id: string;
      file_name: string;
      content_type: TabularContentType;
      sheet_ranges: Record<
        string,
        {
          overall?: [number, number] | null;
          ranges: Array<[number, number]>;
        }
      >;
    }
  | {
      kind: "web";
      title?: string;
      url: string;
      cited_text_preview?: string;
    };

// ---- helpers ----

function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (!ranges?.length) return [];
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [];
  for (const [s, e] of sorted) {
    if (!out.length || s > out[out.length - 1][1] + 1) out.push([s, e]);
    else out[out.length - 1][1] = Math.max(out[out.length - 1][1], e);
  }
  return out;
}

function toNum(n: any): number | null {
  const v = Math.trunc(Number(n));
  return Number.isFinite(v) ? v : null;
}

function toNumPair(
  pair?: [any, any] | null | undefined
): [number, number] | null {
  if (!pair) return null;
  const a = toNum(pair[0]);
  const b = toNum(pair[1]);
  if (a == null || b == null) return null;
  return [a, b];
}

function normalizeDocPages(
  legacyPages?: LegacyDocPages,
  obj?: DocPagesObject
): {
  overall?: [number, number] | null;
  page_ranges: Array<[number, number]>;
  pages: number[];
} {
  if (obj && (obj.overall || obj.page_ranges)) {
    const overall = toNumPair(obj.overall ?? null);
    const ranges = mergeRanges(
      (obj.page_ranges || [])
        .map(toNumPair)
        .filter((p): p is [number, number] => !!p)
    );
    const flat = new Set<number>();
    const source = ranges.length ? ranges : overall ? [overall] : [];
    for (const [s, e] of source) for (let i = s; i <= e; i++) flat.add(i);
    return {
      overall: overall ?? null,
      page_ranges: ranges,
      pages: Array.from(flat).sort((a, b) => a - b),
    };
  }

  // legacy flat pages
  const arr = Array.isArray(legacyPages) ? legacyPages : [];
  const pages: number[] = Array.from(
    new Set<number>(arr.map(toNum).filter((p): p is number => p != null))
  ).sort((a, b) => a - b);

  const ranges: Array<[number, number]> = [];
  let start: number | null = null;
  let prev: number | null = null;
  for (const p of pages) {
    if (start == null) {
      start = p;
      prev = p;
      continue;
    }
    if (p === (prev as number) + 1) {
      prev = p;
      continue;
    }
    ranges.push([start, prev!]);
    start = p;
    prev = p;
  }
  if (start != null && prev != null) ranges.push([start, prev]);

  const overall = ranges.length
    ? ([ranges[0][0], ranges[ranges.length - 1][1]] as [number, number])
    : null;
  return { overall, page_ranges: ranges, pages };
}

function normalizeTabularSheetRanges(
  legacy?: Record<string, Array<[number, number]>>,
  obj?: Record<string, TabularSheetRangesObject>
): Record<
  string,
  { overall?: [number, number] | null; ranges: Array<[number, number]> }
> {
  const out: Record<
    string,
    { overall?: [number, number] | null; ranges: Array<[number, number]> }
  > = {};

  if (obj && Object.keys(obj).length) {
    for (const [sheet, payload] of Object.entries(obj)) {
      const overall = toNumPair(payload?.overall ?? null);
      const ranges = mergeRanges(
        (payload?.ranges || [])
          .map(toNumPair)
          .filter((p): p is [number, number] => !!p)
      );
      out[sheet] = { overall: overall ?? null, ranges };
    }
    return out;
  }

  for (const [sheet, ranges] of Object.entries(legacy || {})) {
    const norm = mergeRanges(
      (ranges || []).map(toNumPair).filter((p): p is [number, number] => !!p)
    );
    const overall = norm.length
      ? ([norm[0][0], norm[norm.length - 1][1]] as [number, number])
      : null;
    out[sheet] = { overall, ranges: norm };
  }
  return out;
}

// ---- main API ----

export function normalizeNewCitationPacket(
  pkt: NewCitationPacket
): CitationViewItem[] {
  if (pkt.type === "web_search") {
    return [
      {
        kind: "web",
        title: pkt.title,
        url: pkt.url,
        cited_text_preview: pkt.cited_text_preview,
      },
    ];
  }

  const items: CitationViewItem[] = [];
  for (const f of pkt.files || []) {
    if (f.content_type === "tabular") {
      const maybeObj =
        (f as any).sheet_ranges_obj ??
        // support object directly under sheet_ranges
        (typeof (f as any).sheet_ranges === "object" &&
        !Array.isArray((f as any).sheet_ranges?.["Sheet1"])
          ? (f as any).sheet_ranges
          : undefined);

      const normalized = normalizeTabularSheetRanges(
        maybeObj ? undefined : (f as any).sheet_ranges,
        maybeObj || undefined
      );

      items.push({
        kind: "tabular",
        file_id: (f as any).file_id,
        file_name: (f as any).file_name,
        content_type: "tabular",
        sheet_ranges: normalized,
      });
    } else {
      const pagesObj: DocPagesObject | undefined =
        (f as any).pages_obj ||
        (f as any).pages_v2 ||
        (f as any).pages_compat ||
        (f as any).pagesNew ||
        (typeof (f as any).pages === "object" &&
        !Array.isArray((f as any).pages)
          ? (f as any).pages
          : undefined);

      const legacyPages: LegacyDocPages | undefined = Array.isArray(
        (f as any).pages
      )
        ? (f as any).pages
        : undefined;

      const { overall, page_ranges, pages } = normalizeDocPages(
        legacyPages,
        pagesObj
      );

      items.push({
        kind: "document",
        file_id: (f as any).file_id,
        file_name: (f as any).file_name,
        content_type: (f as any).content_type,
        overall: overall ?? null,
        page_ranges: page_ranges || [],
        pages: pages || [],
      });
    }
  }
  return items;
}

export function mergeCitationItems(
  base: CitationViewItem[],
  add: CitationViewItem[]
): CitationViewItem[] {
  const keyOf = (it: CitationViewItem) =>
    it.kind === "web"
      ? `web|${it.url}`
      : it.kind === "document"
      ? `doc|${it.file_id}|${it.content_type}`
      : `tab|${it.file_id}|tabular`;

  const map = new Map<string, CitationViewItem>();
  for (const it of [...base, ...add]) {
    const k = keyOf(it);
    const prev = map.get(k);
    if (!prev) {
      map.set(k, JSON.parse(JSON.stringify(it)));
      continue;
    }

    if (prev.kind === "document" && it.kind === "document") {
      const mergedRanges = mergeRanges([
        ...(prev.page_ranges || []),
        ...(it.page_ranges || []),
      ]);
      const overall = mergedRanges.length
        ? ([mergedRanges[0][0], mergedRanges[mergedRanges.length - 1][1]] as [
            number,
            number
          ])
        : prev.overall || it.overall || null;

      const flat = new Set<number>([
        ...(prev.pages || []),
        ...(it.pages || []),
      ]);
      for (const [s, e] of mergedRanges)
        for (let p = s; p <= e; p++) flat.add(p);

      prev.page_ranges = mergedRanges;
      prev.overall = overall;
      prev.pages = Array.from(flat).sort((a, b) => a - b);
    } else if (prev.kind === "tabular" && it.kind === "tabular") {
      for (const [sheet, payload] of Object.entries(it.sheet_ranges || {})) {
        const prevPayload = prev.sheet_ranges[sheet] || {
          overall: null,
          ranges: [] as Array<[number, number]>,
        };
        const mergedRanges = mergeRanges([
          ...(prevPayload.ranges || []),
          ...(payload.ranges || []),
        ]);
        const overall = mergedRanges.length
          ? ([mergedRanges[0][0], mergedRanges[mergedRanges.length - 1][1]] as [
              number,
              number
            ])
          : prevPayload.overall || payload.overall || null;
        prev.sheet_ranges[sheet] = { overall, ranges: mergedRanges };
      }
    }
    // web: dedupe by URL only
  }
  return Array.from(map.values());
}

// ---- pretty labels for tooltips ----

export function formatDocLabel(
  it: Extract<CitationViewItem, { kind: "document" }>
): string {
  if (it.overall) {
    const [s, e] = it.overall;
    return `${it.file_name} — pg. ${s === e ? `${s}` : `${s}–${e}`}`;
  }
  if (it.page_ranges?.length) {
    const parts = it.page_ranges.map(([a, b]) =>
      a === b ? `${a}` : `${a}–${b}`
    );
    return `${it.file_name} — pg. ${parts.join(", ")}`;
  }
  if (it.pages?.length) return `${it.file_name} — pg. ${it.pages.join(", ")}`;
  return it.file_name;
}

export function formatTabularLabel(
  it: Extract<CitationViewItem, { kind: "tabular" }>
): string {
  const parts: string[] = [];
  for (const [sheet, payload] of Object.entries(it.sheet_ranges || {})) {
    if (payload.overall) {
      const [s, e] = payload.overall;
      parts.push(`${sheet} rows ${s === e ? `${s}` : `${s}–${e}`}`);
    } else if (payload.ranges?.length) {
      parts.push(
        `${sheet} rows ${payload.ranges
          .map(([a, b]) => (a === b ? `${a}` : `${a}–${b}`))
          .join(", ")}`
      );
    } else {
      parts.push(sheet);
    }
  }
  return parts.length ? `${it.file_name} — ${parts.join("; ")}` : it.file_name;
}
